import cn from 'classnames';
import { useState } from 'react';

import { CouponResult } from '@zooplus/checkout-state';
import { Anchor, P1 } from '@zooplus/ui-components';

import CouponsInfoModal from '../../../cart/coupons-info-modal';
import AlertComponent from '../alert-component';

import { useTranslation } from '@hooks/use-translations';
import { CART_MESSAGE_ACTION_STYLE, PAGE_PATH } from '@utilities/constants';
import { HTMLParser } from '@utilities/html-parser';
import { getCartMessageActionData, handleKeyboardKeyDown } from '@utilities/utils';

import styles from './feedback-message.module.scss';

interface Message {
  blockedOnOverview?: boolean;
  code: string;
  config?: {
    actionStyle?: string;
    actionType?: string;
    closeButton?: boolean;
    iconType?: string;
    showAction?: boolean;
    temporary?: boolean;
  };
  couponCode?: string;
  invalidateCart?: boolean;
  localizedMessage?: string;
  localizedMoreMessage?: string;
  messageParams?: string[];
  messageKey: string;
  placements: string[];
  showAsSnackbar?: boolean;
  showMoreMessage?: boolean;
  showOnlyOnce?: boolean;
  type?: string;
  uiType: 'error' | 'caution' | 'info' | 'success' | 'warning' | undefined;
}

type FeedbackComponentProps = {
  message: Message;
  index: string;
  dataTestId: string;
  customStyle?: string;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  couponResult?: CouponResult;
  handleCouponAddition?: (couponCode?: string) => void;
  handleCouponRemoval?: (couponCode?: string) => void;
};

const FeedbackComponent = ({
  message,
  index,
  dataTestId,
  customStyle,
  isActive,
  setIsActive,
  couponResult,
  handleCouponAddition,
  handleCouponRemoval,
}: FeedbackComponentProps) => {
  const { t } = useTranslation();
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

  const showAction = message?.config?.showAction;
  const actionStyle = message?.config?.actionStyle;
  const actionType = message?.config?.actionType;
  const closeButton = message?.config?.closeButton;
  const temporary = message?.config?.temporary;

  const { handleOnClick, cartMessageActionTestId, label } = getCartMessageActionData(
    t,
    actionType,
    handleCouponAddition,
    handleCouponRemoval,
  );

  const messageParams = message.messageParams?.reduce(
    (obj, item, index) => Object.assign(obj, { [index]: item }),
    {},
  );

  if (
    message.messageKey === 'checkout.placeorder.error.restrictedDeliveryCountry' &&
    message.messageParams
  ) {
    return (
      <AlertComponent
        dataTestId={dataTestId}
        key={index}
        customStyle={cn(customStyle, styles.commonStyle)}
        variantType={message?.uiType}
        isActive={isActive}
        setActiveState={setIsActive}
        message={
          <>
            <P1>
              {t('checkout.delivery.error.restrictedDeliveryCountry', {
                0: message.messageParams[0],
                interpolation: { escapeValue: false },
              })}
            </P1>
            <ul className={styles.productList} data-testid="restricted-product-list">
              {message.messageParams[1].split(',').map((product, productIndex) => (
                <li key={`product-${productIndex.toString()}`}>
                  <P1>{product}</P1>
                </li>
              ))}
            </ul>
            <P1>
              {`${t('checkout.delivery.restrictedDeliveryItems')} `}
              <Anchor
                data-testid="edit-basket-link"
                href={PAGE_PATH.CART}
                underline
                variant="neutral">
                {t('checkout.delivery.restrictedDeliveryCart')}
              </Anchor>
            </P1>
          </>
        }
      />
    );
  } else {
    return (
      <AlertComponent
        dataTestId={dataTestId}
        key={index}
        customStyle={cn(customStyle, styles.commonStyle)}
        variantType={message?.uiType}
        isActive={isActive}
        setActiveState={setIsActive}
        hideAlert={temporary}
        showCloseButton={closeButton}
        couponCode={message?.couponCode}
        buttonActionType={
          showAction && actionStyle === CART_MESSAGE_ACTION_STYLE.BUTTON ? actionType : ''
        }
        showActionButton={showAction && actionStyle === CART_MESSAGE_ACTION_STYLE.BUTTON}
        message={
          <>
            {!message?.localizedMessage ? (
              <HTMLParser content={t(message?.messageKey, messageParams)} />
            ) : (
              <HTMLParser content={message?.localizedMessage} />
            )}
            {couponResult && showAction && actionStyle === CART_MESSAGE_ACTION_STYLE.LINK && (
              <Anchor
                isBold
                tabIndex={0}
                onClick={() => handleOnClick?.()}
                onKeyDown={(event) =>
                  handleKeyboardKeyDown(event, handleCouponRemoval, couponResult?.couponCode)
                }
                data-testid={cartMessageActionTestId}>
                {label}
              </Anchor>
            )}
            {message?.showMoreMessage && (
              <span data-testid="show-more-link">
                <Anchor
                  isBold
                  tabIndex={0}
                  onClick={() => setShowInfoModal(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setShowInfoModal(true);
                    }
                  }}>
                  {t('loy.coupons.component.show.more')}
                </Anchor>
                <CouponsInfoModal
                  modalBody={message?.localizedMoreMessage || ''}
                  isModalOpen={showInfoModal}
                  callback={setShowInfoModal}
                />
              </span>
            )}
          </>
        }
      />
    );
  }
};

export default FeedbackComponent;
